import React, { useCallback, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { Checkbox, Chip, ListItemIcon, ListItemText, MenuItem, CircularProgress } from '@material-ui/core'
import SydModal from '../../commonDesign/SydModal'
import SydModalActions from '../../commonDesign/SydModal/SydModalActions'
import SydButton from '../../commonDesign/Button'
import SydSelect from '../../commonDesign/SydSelect'
import SydInput from '../../commonDesign/SydInput'
import Icon from '../../atoms/Icon'
import { useGetBlueprintFacetValues } from '../../../api/denali'
import { OPERATORS_BY_TYPE, RANGE_OPERATORS, describeFilter } from './filterUtils'

const styles = {
  activeFilters: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--dim-xs)',
    marginBottom: 'var(--dim-md)'
  },
  activeFilterRow: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    padding: 'var(--dim-xs) var(--dim-sm)',
    background: 'var(--bg-color-secondary)',
    borderRadius: 'var(--dim-xs)',
    fontSize: 'var(--text-size-sm)',
    gap: 'var(--dim-sm)'
  },
  activeFilterContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    flex: 1,
    minWidth: 0
  },
  chipRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '4px'
  },
  addSection: {
    borderTop: '1px solid var(--border-color-main)',
    paddingTop: 'var(--dim-md)'
  },
  fieldRow: {
    display: 'flex',
    gap: 'var(--dim-sm)',
    alignItems: 'flex-end',
    marginBottom: 'var(--dim-sm)'
  },
  rangeRow: {
    display: 'flex',
    gap: 'var(--dim-sm)',
    alignItems: 'center',
    marginBottom: 'var(--dim-sm)',
    overflow: 'hidden'
  },
  facetList: {
    maxHeight: '200px',
    overflowY: 'auto',
    border: '1px solid var(--border-color-main)',
    borderRadius: 'var(--dim-xs)',
    marginTop: 'var(--dim-xs)'
  },
  facetItem: {
    padding: '2px 8px'
  },
  facetLabel: {
    fontSize: '0.875rem'
  }
}

function FacetedFilterInput ({ blueprintCode, purposeCode, column, value, onChange }) {
  const { data, isLoading } = useGetBlueprintFacetValues(
    blueprintCode, purposeCode, column.name
  )
  const facetValues = data?.values ?? []
  const selected = useMemo(() => new Set(value || []), [value])

  const handleToggle = useCallback((val) => {
    const next = new Set(selected)
    if (next.has(val)) {
      next.delete(val)
    } else {
      next.add(val)
    }
    onChange(Array.from(next))
  }, [selected, onChange])

  if (isLoading) {
    return (
      <div style={{ padding: 'var(--dim-sm)', textAlign: 'center' }}>
        <CircularProgress size={20} />
      </div>
    )
  }

  return (
    <div style={styles.facetList}>
      {facetValues.map((val) => (
        <MenuItem
          key={String(val)}
          style={styles.facetItem}
          onClick={() => handleToggle(val)}
        >
          <ListItemIcon style={{ minWidth: 36 }}>
            <Checkbox
              checked={selected.has(val)}
              size='small'
              color='primary'
            />
          </ListItemIcon>
          <ListItemText
            primary={val == null ? '(empty)' : String(val)}
            primaryTypographyProps={{ style: styles.facetLabel }}
          />
        </MenuItem>
      ))}
      {facetValues.length === 0
        ? <div style={{ padding: 'var(--dim-sm)', color: 'var(--text-color-subtle)', fontSize: 'var(--text-size-sm)' }}>No values found</div>
        : null}
    </div>
  )
}

FacetedFilterInput.propTypes = {
  blueprintCode: PropTypes.string.isRequired,
  purposeCode: PropTypes.string.isRequired,
  column: PropTypes.object.isRequired,
  value: PropTypes.array,
  onChange: PropTypes.func.isRequired
}

function ActiveFilterRow ({ filter, columnMap, onRemove }) {
  const col = columnMap.get(filter.column)
  const colLabel = col?.displayName || col?.name || filter.column

  if (filter.operator === 'in' && Array.isArray(filter.value)) {
    return (
      <div style={styles.activeFilterRow}>
        <div style={styles.activeFilterContent}>
          <span>{colLabel} is:</span>
          <div style={styles.chipRow}>
            {filter.value.map((v) => (
              <Chip key={String(v)} label={String(v)} size='small' variant='outlined' />
            ))}
          </div>
        </div>
        <SydButton variant='ghost' size='sm' onClick={onRemove}>
          <Icon name='close' />
        </SydButton>
      </div>
    )
  }

  return (
    <div style={styles.activeFilterRow}>
      <span>{describeFilter(filter, columnMap)}</span>
      <SydButton variant='ghost' size='sm' onClick={onRemove}>
        <Icon name='close' />
      </SydButton>
    </div>
  )
}

ActiveFilterRow.propTypes = {
  filter: PropTypes.object.isRequired,
  columnMap: PropTypes.instanceOf(Map).isRequired,
  onRemove: PropTypes.func.isRequired
}

function DynamicFilterModal ({
  open,
  onClose,
  columns,
  blueprintCode,
  purposeCode,
  activeFilters,
  onApply
}) {
  const [localFilters, setLocalFilters] = useState([])
  const [addColumn, setAddColumn] = useState('')
  const [addOperator, setAddOperator] = useState('')
  const [addValue, setAddValue] = useState('')
  const [addRangeFrom, setAddRangeFrom] = useState('')
  const [addRangeTo, setAddRangeTo] = useState('')
  const [addFacetValues, setAddFacetValues] = useState([])

  const resetAddForm = useCallback(() => {
    setAddColumn('')
    setAddOperator('')
    setAddValue('')
    setAddRangeFrom('')
    setAddRangeTo('')
    setAddFacetValues([])
  }, [])

  useEffect(() => {
    if (open) {
      setLocalFilters(activeFilters || [])
      resetAddForm()
    }
  }, [open, activeFilters, resetAddForm])

  const columnMap = useMemo(() => {
    return new Map(columns.map(c => [c.name, c]))
  }, [columns])

  const filterableColumns = useMemo(() => {
    return columns.filter(c => c.filterable && !c.exclude)
  }, [columns])

  const columnOptions = useMemo(() => {
    return filterableColumns.map(c => ({
      label: c.displayName || c.name,
      value: c.name
    }))
  }, [filterableColumns])

  const selectedColumnDef = addColumn ? columnMap.get(addColumn) : null
  const isFaceted = selectedColumnDef?.faceted
  const dataType = selectedColumnDef?.dataType || 'string'

  const operatorOptions = useMemo(() => {
    if (!dataType || isFaceted) return []
    return OPERATORS_BY_TYPE[dataType] || OPERATORS_BY_TYPE.string
  }, [dataType, isFaceted])

  const isRange = RANGE_OPERATORS.has(addOperator)

  const handleColumnChange = useCallback((e) => {
    setAddColumn(e.target.value)
    setAddOperator('')
    setAddValue('')
    setAddRangeFrom('')
    setAddRangeTo('')
    setAddFacetValues([])
  }, [])

  const handleOperatorChange = useCallback((e) => {
    setAddOperator(e.target.value)
    setAddValue('')
    setAddRangeFrom('')
    setAddRangeTo('')
  }, [])

  const canAdd = useMemo(() => {
    if (!addColumn) return false
    if (isFaceted) return addFacetValues.length > 0
    if (!addOperator) return false
    if (isRange) return addRangeFrom !== '' && addRangeTo !== ''
    return addValue !== ''
  }, [addColumn, isFaceted, addOperator, isRange, addValue, addRangeFrom, addRangeTo, addFacetValues])

  const parseTypedValue = useCallback((raw, dataType) => {
    if (dataType === 'integer') return parseInt(raw, 10)
    if (dataType === 'decimal') return parseFloat(raw)
    if (dataType === 'boolean') return raw === 'true'
    return raw
  }, [])

  const buildPendingFilter = useCallback(() => {
    if (!canAdd) return null
    if (isFaceted) {
      return { column: addColumn, operator: 'in', value: addFacetValues }
    }
    const colDef = columnMap.get(addColumn)
    const dt = colDef?.dataType
    if (isRange) {
      return { column: addColumn, operator: addOperator, value: [parseTypedValue(addRangeFrom, dt), parseTypedValue(addRangeTo, dt)] }
    }
    return { column: addColumn, operator: addOperator, value: parseTypedValue(addValue, dt) }
  }, [canAdd, isFaceted, isRange, addColumn, addOperator, addValue, addRangeFrom, addRangeTo, addFacetValues, columnMap, parseTypedValue])

  const handleAddFilter = useCallback(() => {
    const filter = buildPendingFilter()
    if (!filter) return
    setLocalFilters(prev => [...prev, filter])
    resetAddForm()
  }, [buildPendingFilter, resetAddForm])

  const handleRemoveFilter = useCallback((index) => {
    setLocalFilters(prev => prev.filter((_, i) => i !== index))
  }, [])

  const formIsPartial = !!addColumn && !canAdd

  const handleApply = useCallback(() => {
    const pending = buildPendingFilter()
    const all = pending ? [...localFilters, pending] : localFilters
    onApply(all)
    onClose()
  }, [localFilters, buildPendingFilter, onApply, onClose])

  const handleClearAll = useCallback(() => {
    onApply([])
    onClose()
  }, [onApply, onClose])

  const inputType = (dataType === 'integer' || dataType === 'decimal') ? 'number' : 'text'

  return (
    <SydModal
      title='Filters'
      open={open}
      onClose={onClose}
      size='xmedium'
    >
      <div style={{ padding: '0 0 var(--dim-md) 0' }}>
        {localFilters.length > 0
          ? (
            <div style={styles.activeFilters}>
              {localFilters.map((f, idx) => (
                <ActiveFilterRow
                  key={idx}
                  filter={f}
                  columnMap={columnMap}
                  onRemove={() => handleRemoveFilter(idx)}
                />
              ))}
            </div>
          )
          : null}

        <div style={styles.addSection}>
          <div className='font-size-sm' style={{ fontWeight: 600, marginBottom: 'var(--dim-sm)' }}>
            Add Filter
          </div>

          <div style={styles.fieldRow}>
            <div style={{ flex: 1 }}>
              <SydSelect
                options={columnOptions}
                value={addColumn}
                onChange={handleColumnChange}
                placeholder='Column'
                size='sm'
              />
            </div>
          </div>

          {selectedColumnDef && !isFaceted
            ? (
              <>
                <div style={styles.fieldRow}>
                  <div style={{ flex: 1 }}>
                    <SydSelect
                      options={operatorOptions}
                      value={addOperator}
                      onChange={handleOperatorChange}
                      placeholder='Operator'
                      size='sm'
                    />
                  </div>
                  {addOperator && !isRange
                    ? (
                      <div style={{ flex: 1 }}>
                        {dataType === 'boolean'
                          ? (
                            <SydSelect
                              options={[
                                { label: 'True', value: 'true' },
                                { label: 'False', value: 'false' }
                              ]}
                              value={addValue}
                              onChange={(e) => setAddValue(e.target.value)}
                              placeholder='Value'
                              size='sm'
                            />
                          )
                          : (
                            <SydInput
                              value={addValue}
                              onChange={(e) => setAddValue(e.target.value)}
                              placeholder='Value'
                              size='sm'
                              type={inputType}
                            />
                          )}
                      </div>
                    )
                    : null}
                </div>
                {addOperator && isRange
                  ? (
                    <div style={styles.rangeRow}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <SydInput
                          value={addRangeFrom}
                          onChange={(e) => setAddRangeFrom(e.target.value)}
                          placeholder='From'
                          size='sm'
                          type={inputType}
                        />
                      </div>
                      <span className='font-size-sm' style={{ color: 'var(--text-color-subtle)', flexShrink: 0 }}>to</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <SydInput
                          value={addRangeTo}
                          onChange={(e) => setAddRangeTo(e.target.value)}
                          placeholder='To'
                          size='sm'
                          type={inputType}
                        />
                      </div>
                    </div>
                  )
                  : null}
              </>
            )
            : null}

          {selectedColumnDef && isFaceted
            ? (
              <FacetedFilterInput
                blueprintCode={blueprintCode}
                purposeCode={purposeCode}
                column={selectedColumnDef}
                value={addFacetValues}
                onChange={setAddFacetValues}
              />
            )
            : null}
        </div>

        {(localFilters.length > 0 || canAdd)
          ? (
            <div style={{ marginTop: 'var(--dim-sm)' }}>
              <SydButton
                variant='ghost'
                size='sm'
                onClick={handleAddFilter}
                disabled={!canAdd}
              >
                + Add Another
              </SydButton>
            </div>
          )
          : null}
      </div>

      <SydModalActions>
        <SydButton
          variant='ghost'
          size='md'
          onClick={handleClearAll}
          disabled={localFilters.length === 0}
        >
          Clear All
        </SydButton>
        <SydButton variant='primary' size='md' onClick={handleApply} disabled={formIsPartial && localFilters.length === 0}>
          Apply
        </SydButton>
      </SydModalActions>
    </SydModal>
  )
}

DynamicFilterModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  columns: PropTypes.array.isRequired,
  blueprintCode: PropTypes.string.isRequired,
  purposeCode: PropTypes.string.isRequired,
  activeFilters: PropTypes.array,
  onApply: PropTypes.func.isRequired
}

DynamicFilterModal.defaultProps = {
  activeFilters: []
}

export default DynamicFilterModal
